import { useEffect, useMemo, useRef, useState } from 'react'
import { Typography, HtmlTypography } from '@frontend/design_system'
import { joinClasses } from 'utils/joinClasses'
import { highlightMatch, getMatch } from 'utils/match'
import { CheckIcon } from 'ui/icons'
import { ListProps } from './types'
import { NOT_FOUND } from 'texts/uiTexts'
import { useDidUpdate } from 'hooks/useDidUpdate'
import styles from './styles.module.scss'

const ITEM_ID = 'item'

export const AutocompleteList = ({
  items,
  selectedValue,
  onChange,
  containerRef,
  notFoundTitle,
  dataTestId,
}: ListProps) => {
  const listRef = useRef<HTMLUListElement | null>(null)
  const [isFirstRender, setIsFirstRender] = useState(true)

  useDidUpdate(() => {
    setIsFirstRender(false)
  }, [selectedValue])

  const filteredItems = useMemo(() => {
    if (!items.length) return []

    const selectedItemIndex = items.findIndex(
      (item) => item.label === selectedValue
    )

    if (selectedItemIndex !== -1) {
      const selectedItem = items[selectedItemIndex]
      const otherItems = items.filter((_, id) => id !== selectedItemIndex)
      return [selectedItem, ...otherItems]
    } else {
      const matchedItems = items.filter((item) =>
        getMatch(selectedValue, item.label)
      )
      const otherItems = items.filter(
        (item) => !matchedItems.some((matched) => matched.label === item.label)
      )
      return isFirstRender || !selectedValue
        ? [...matchedItems, ...otherItems]
        : matchedItems
    }
  }, [items, selectedValue, isFirstRender])

  useEffect(() => {
    const element = document.getElementById(`${ITEM_ID}-${selectedValue}`)

    if (element && containerRef.current) {
      const { top: containerTop } = containerRef.current.getBoundingClientRect()
      const { top, height } = element.getBoundingClientRect()

      if (listRef.current) {
        listRef.current.scrollTo({
          top: top - containerTop - height,
        })
      }
    }
  }, [])

  return (
    <div data-testid={dataTestId}>
      {filteredItems.length ? (
        <ul ref={listRef} className={styles['autocomplete-list']}>
          {filteredItems.map((item, i) => {
            const key = i
            const isSelected = item?.label === selectedValue
            const handleChange = () => onChange(item?.label, item)

            return (
              <Typography
                key={key}
                id={`${ITEM_ID}-${item.value}`}
                name="Button2"
                className={joinClasses(styles['autocomplete-list-item'], [
                  styles.selected,
                  isSelected,
                ])}
                Tag="li"
                onClick={handleChange}
                dataTestId={`${dataTestId}-item-${i}`}
              >
                <div className={styles['autocomplete-list-item-content']}>
                  {item.renderLabel?.(item.label) || (
                    <HtmlTypography
                      html={highlightMatch(item.label, selectedValue, 'mark')}
                      name="Button2"
                      markColor="blackMaster"
                      color={selectedValue ? 'grey400' : 'blackMaster'}
                    />
                  )}
                  {isSelected && (
                    <CheckIcon
                      color="green700Master"
                      className={styles['autocomplete-list-icon']}
                      size="small"
                    />
                  )}
                </div>
              </Typography>
            )
          })}
        </ul>
      ) : (
        <Typography
          name="Button2"
          color="blueSecondary"
          Tag="div"
          className={styles['not-found']}
        >
          {notFoundTitle || NOT_FOUND.TITLE}
        </Typography>
      )}
    </div>
  )
}
